import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
    Backdrop,
    CircularProgress,
    Button
} from '@material-ui/core';
import PrintIcon from '@material-ui/icons/Print';
import Moment from 'moment';
import Page from 'src/components/Page';
import { getUser, moneyFormat } from 'src/utils/Common';
import { paymentActions, paymentThunks } from 'src/store/slices/paymentSlice';
import { printProformaInvoice } from 'src/views/Sales/ProductSales/ProformaInvoice';

const ProductOrderDetail = ({ OrderId,consumerId }) => {
    const { t } = useTranslation(['overview']);
    const dispatch = useDispatch();
    const [orderInformation, setOrderInformation] = useState({});
    const [consumerInfo, setConsumerInfo] = useState({});
    const [productInfo, setProductInfo] = useState({});
    const [paymentInfo, setPaymentInfo] = useState({});
    const [Loading, setLoading] = useState(false);
    const [generatingProforma, setGeneratingProforma] = useState(false);
    const [rawConsumer, setRawConsumer] = useState(null);
    const [rawOrder, setRawOrder] = useState(null);
    const SessionUser = getUser();

    Moment.locale(SessionUser.TenantCode === 'my' ? 'en' : SessionUser.TenantCode);
    
    const useStyles = makeStyles(theme => ({
        root: {},
        content: {
            minHeight: 430
        },
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
            color: '#fff'
        },
        sectionCard: {
            maxHeight: 600,
            minHeight: 600
        },
        sectionTitle: {
            fontWeight: 'bold',
            marginBottom: theme.spacing(2)
        },
        fieldLabel: {
            marginTop: theme.spacing(1.5),
            marginBottom: theme.spacing(0.5)
        }
    }));

    const classes = useStyles();

    useEffect(() => {
        setLoading(true);
        const abortController = new AbortController();

        if (OrderId === undefined || OrderId === 0 || OrderId === '0') {
            setOrderInformation({});
            setConsumerInfo({});
            setProductInfo({});
            setPaymentInfo({});
        } else {
            setLoading(true);
            dispatch(paymentThunks.GetConsumerInfo(consumerId))
                .then(response => {
                    if (!abortController.signal.aborted) {
                        const consumer = response.payload.ResponseObject;
                        setRawConsumer(consumer);
                        setConsumerInfo({
                            ConsumerFullName: `${consumer.Name} ${consumer.Surname}`,
                            ConsumerEmail: consumer.CustomerEmail,
                            ContactPhone: consumer.CustomerPhone,
                            Address: consumer.Address?.Address || '',
                        });
                    }
                })
                .catch(() => {
                    if (!abortController.signal.aborted) {
                        setConsumerInfo({});
                    }
                });

            dispatch(paymentThunks.GetProductOrderDetails(OrderId))
                .then(response => {
                    if (!abortController.signal.aborted) {
                        const order = response.payload;
                        setRawOrder(order);
                        setOrderInformation({
                            ProductOrderStatusName: order.ProductOrderStatusName,
                            WorkOrderId: order.WorkOrderId,
                            CreateDate: order.CreateDate,
                            ErpDocumentNr: order.ErpDocumentNr,
                            ProductSku: order.ProductSku,
                            Provider: order.Provider,
                            Method: order.Method
                        });
                    }
                })
                .catch(() => {
                    if (!abortController.signal.aborted) {
                        setOrderInformation({});
                    }
                });
               
            setLoading(false);
        }

        return () => {
            abortController.abort();
        };
    }, [OrderId, dispatch]);

    const handleGenerateProforma = () => {
        setGeneratingProforma(true);
        setTimeout(() => {
            try {
                debugger
                printProformaInvoice({
                    sku: rawOrder?.ProductSku || '',
                    productName: rawOrder?.ProductName || '',
                    price: rawOrder?.TotalAmountPaid,
                    currency: rawOrder?.Currency || SessionUser.CurrencyUnit || '',
                    customerName: rawConsumer?.Name || '',
                    customerSurname: rawConsumer?.Surname || '',
                    customerEmail: rawConsumer?.CustomerEmail || '',
                    customerPhone: rawConsumer?.CustomerPhone || '',
                    cuiNumber: rawConsumer?.CuiNumber || '',
                    city: rawConsumer?.Address?.City || '',
                    street: rawConsumer?.Address?.Street || '',
                    postCode: rawConsumer?.Address?.PostCode || rawConsumer?.Address?.ZipCode || '',
                    addressFree: rawConsumer?.Address?.Address || '',
                    workOrderId: rawOrder?.WorkOrderId || '',
                    vatRate: 21,
                    quantity: 1,
                    productOrderId: OrderId || null
                });
            } catch (error) {
                console.error('Proforma generation error:', error);
            } finally {
                setGeneratingProforma(false);
            }
        }, 500);
    };

    return (
        <div>
            <Backdrop className={classes.backdrop} open={Loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Container maxWidth="xl">
                <Card>
                    <CardContent>
                        <Grid container spacing={3}>
                            {/* CUSTOMER INFO SECTION */}
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <Card className={classes.sectionCard}>
                                    <CardHeader
                                        style={{ textAlign: 'left' }}
                                        title={t('lblCustomerInfo')}
                                        action={<Box p={2.5} />}
                                    />
                                    <Divider />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            {/* Customer fields will be inserted here */}
                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblCustomerName')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {consumerInfo.ConsumerFullName}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblCustomerEmail')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {consumerInfo.ConsumerEmail}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblContactPhone')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {consumerInfo.ContactPhone}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblAddress')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {consumerInfo.Address}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>



                            {/* Product Order  INFO SECTION */}
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <Card className={classes.sectionCard}>
                                    <CardHeader
                                        style={{ textAlign: 'left' }}
                                        title={t('lblOrderDetails')}
                                        action={<Box p={2.5} />}
                                    />
                                    <Divider />
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblOrderStatus')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.ProductOrderStatusName}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblWorkOrderId')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.WorkOrderId}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblCreateDate')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.CreateDate === undefined
                                                        ? ''
                                                        : Moment(orderInformation.CreateDate).format('DD/MM/YYYY')}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblErpDocumentNr')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.ErpDocumentNr}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblProductSku')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.ProductSku}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblProvider')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.Provider}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Typography
                                                    className={classes.fieldLabel}
                                                    color="textSecondary"
                                                    variant="body2"
                                                >
                                                    {t('lblMethod')}
                                                </Typography>
                                                <Typography variant="h6" component="h2">
                                                    {orderInformation.Method}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={generatingProforma ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                        onClick={handleGenerateProforma}
                        disabled={generatingProforma || !rawOrder}
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '10px 24px',
                            borderRadius: 12
                        }}
                    >
                        {generatingProforma ? 'Generating...' : 'Print Proforma Invoice'}
                    </Button>
                </Box>
            </Container>
        </div>
    );
};

export default ProductOrderDetail;
